import { useQuery } from '@tanstack/react-query';
import type {
  EtfHoldingsDto,
  FlowEtfListDto,
  MarketCrowdingDto,
  MarketDebriefDto,
  MarketFlowDto,
  MarketNowDto,
  MarketPulseDto,
  MarketStocksDto,
  MarketUniverseDto,
  MarketScreenDto,
  ScanSettingsDto,
  ScreenFilters,
  ScreenResultDto,
  StockGroupDto,
} from 'shared';

import { apiClient } from '../../lib/api/client';

export const marketConsoleKeys = {
  all: ['market-console'] as const,
  now: ['market-console', 'now'] as const,
  debrief: ['market-console', 'debrief'] as const,
  pulse: ['market-console', 'pulse'] as const,
  crowding: ['market-console', 'crowding'] as const,
  stocks: ['market-console', 'stocks'] as const,
  universe: ['market-console', 'stocks', 'universe'] as const,
  scanSettings: ['market-console', 'scan-settings'] as const,
  screens: ['market-console', 'screens'] as const,
  screener: ['market-console', 'screener'] as const,
  stockGroups: ['market-console', 'stock-groups'] as const,
  flow: ['market-console', 'flow'] as const,
  flowEtfs: ['market-console', 'flow', 'etfs'] as const,
  holdings: (symbol: string) => ['market-console', 'flow', 'holdings', symbol] as const,
};

/** Query key segment for a replay date; live data shares the `live` segment. */
function replayKey(asOf: string | undefined): string {
  return asOf ?? 'live';
}

async function getConsole<T>(path: string, asOf: string | undefined): Promise<T> {
  const { data } = asOf
    ? await apiClient.get<T>(path, { params: { asOf } })
    : await apiClient.get<T>(path);
  return data;
}

export function useMarketNow(asOf?: string) {
  return useQuery({
    queryKey: [...marketConsoleKeys.now, replayKey(asOf)],
    queryFn: () => getConsole<MarketNowDto>('/market-console/now', asOf),
  });
}

export function useMarketDebrief(asOf?: string) {
  return useQuery({
    queryKey: [...marketConsoleKeys.debrief, replayKey(asOf)],
    queryFn: () => getConsole<MarketDebriefDto>('/market-console/debrief', asOf),
  });
}

export function useMarketPulse(asOf?: string) {
  return useQuery({
    queryKey: [...marketConsoleKeys.pulse, replayKey(asOf)],
    queryFn: () => getConsole<MarketPulseDto>('/market-console/pulse', asOf),
  });
}

export function useMarketCrowding(asOf?: string) {
  return useQuery({
    queryKey: [...marketConsoleKeys.crowding, replayKey(asOf)],
    queryFn: () => getConsole<MarketCrowdingDto>('/market-console/crowding', asOf),
  });
}

export function useMarketStocks(asOf?: string) {
  return useQuery({
    queryKey: [...marketConsoleKeys.stocks, replayKey(asOf)],
    queryFn: () => getConsole<MarketStocksDto>('/market-console/stocks', asOf),
  });
}

export function useMarketUniverse(asOf: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...marketConsoleKeys.universe, replayKey(asOf)],
    queryFn: () => getConsole<MarketUniverseDto>('/market-console/stocks/universe', asOf),
    enabled,
  });
}

export function useScanSettings() {
  return useQuery({
    queryKey: marketConsoleKeys.scanSettings,
    queryFn: async () => {
      const { data } = await apiClient.get<ScanSettingsDto>('/market-console/scan-settings');
      return data;
    },
  });
}

export function useMarketScreens() {
  return useQuery({
    queryKey: marketConsoleKeys.screens,
    queryFn: async () => {
      const { data } = await apiClient.get<{ screens: MarketScreenDto[] }>(
        '/market-console/screens',
      );
      return data.screens;
    },
  });
}

export function useScreenResult(filters: ScreenFilters, asOf: string | undefined) {
  return useQuery({
    queryKey: [...marketConsoleKeys.screener, replayKey(asOf), filters],
    queryFn: async () => {
      const { data } = await apiClient.post<ScreenResultDto>(
        '/market-console/screener/run',
        { filters },
        asOf ? { params: { asOf } } : undefined,
      );
      return data;
    },
  });
}

export function useMarketFlow(asOf?: string) {
  return useQuery({
    queryKey: [...marketConsoleKeys.flow, replayKey(asOf)],
    queryFn: () => getConsole<MarketFlowDto>('/market-console/flow', asOf),
  });
}

async function getEtfHoldings(symbol: string): Promise<EtfHoldingsDto> {
  const { data } = await apiClient.get<EtfHoldingsDto>(
    `/market-console/flow/holdings/${encodeURIComponent(symbol)}`,
  );
  return data;
}

export function useEtfHoldings(symbol: string | null) {
  return useQuery({
    queryKey: marketConsoleKeys.holdings(symbol ?? ''),
    queryFn: () => getEtfHoldings(symbol ?? ''),
    enabled: symbol !== null,
  });
}

async function getFlowEtfs(): Promise<FlowEtfListDto> {
  const { data } = await apiClient.get<FlowEtfListDto>('/market-console/flow/etfs');
  return data;
}

export function useFlowEtfs() {
  return useQuery({ queryKey: marketConsoleKeys.flowEtfs, queryFn: getFlowEtfs });
}

export function useStockGroups() {
  return useQuery({
    queryKey: marketConsoleKeys.stockGroups,
    queryFn: async () => {
      const { data } = await apiClient.get<{ groups: StockGroupDto[] }>(
        '/market-console/stock-groups',
      );
      return data.groups;
    },
  });
}
